import { Injectable, signal, computed, OnDestroy, inject, effect } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { DemoModeService } from './demo-mode.service';
import { GraphQLSubscriptionClient } from '../graphql/subscription-client';

const HEARTBEAT_ONLINE_MS = 30_000;
const HEARTBEAT_OFFLINE_MS = 10_000;
const HEARTBEAT_TIMEOUT_MS = 5_000;
const BACK_ONLINE_DISPLAY_MS = 3_000;
// Grace before flagging dead realtime — same window as the Selects offline
// banner precedent; short blips (deploys, wifi roaming) stay invisible.
// Counts VISIBLE time only: a hidden or bfcached page cannot reconnect
// (throttled timers; the browser kills the socket on back/forward-cache entry),
// so burning the window while nobody is looking would guarantee a banner flash
// on every return while the wake-driven reconnect is still in flight.
const REALTIME_GRACE_MS = 30_000;

export type ConnectivityState = 'online' | 'offline' | 'server-unreachable';

/**
 * Monitors network connectivity using browser events + server heartbeat.
 *
 * Distinguishes two failure modes:
 * - `offline` — browser has no network adapter connection (navigator.onLine === false)
 * - `server-unreachable` — browser is online but the API server doesn't respond
 *
 * Additionally tracks `realtimeDegraded` — HTTP is fine but the GraphQL
 * WebSocket has been down past a grace period of VISIBLE time while a stream
 * wants it, so live updates are paused (the topbar renders it as a quiet
 * reconnecting pill — never a full-width bar: everything still works in that
 * state, only others' changes stop arriving live). Hidden/bfcached downtime
 * never counts toward the grace: the client cannot reconnect while hidden, and
 * the pill only claims "Reconnecting…" once a user-visible reconnect window has
 * actually elapsed.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService implements OnDestroy {
  private readonly demoModeService = inject(DemoModeService);
  private readonly subscriptionClient = inject(GraphQLSubscriptionClient);

  readonly state = signal<ConnectivityState>('online');
  readonly showBackOnline = signal(false);
  readonly recoveredFrom = signal<'offline' | 'server-unreachable' | null>(null);
  readonly realtimeDegraded = signal(false);
  /**
   * The reconnecting state the topbar's pill shows: live updates paused AND the server
   * reachable. Under the offline or server-unreachable bar the bar speaks for the outage —
   * a second indicator for the same outage would be noise (Robert's ruling of 2026-09-29,
   * at the small as-built review's close); the pill returns the moment the server is back
   * while the socket is still catching up.
   */
  readonly reconnecting = computed(() => this.realtimeDegraded() && this.state() === 'online');

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private backOnlineTimer: ReturnType<typeof setTimeout> | null = null;
  private realtimeGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private realtimeDown = false;
  private wsSubscription: Subscription | null = null;
  private readonly onOnline = () => this.checkConnectivity();
  private readonly onOffline = () => this.setDisconnected('offline');
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      // Pause the countdown — hidden downtime is invisible to the user and
      // unrecoverable by the client, so it must not count against the grace.
      this.clearRealtimeGrace();
    } else if (this.realtimeDown && !this.realtimeDegraded()
      && this.subscriptionClient.activeConsumerStreams() !== 0) {
      // Back in view with realtime still down: the reconnect gets a fresh
      // grace window to win before the pill shows.
      this.armRealtimeGrace();
    }
  };

  constructor() {
    if (this.demoModeService.isDemo()) return;

    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (!navigator.onLine) {
      this.state.set('offline');
    }

    this.startHeartbeat(HEARTBEAT_ONLINE_MS);

    // connected$ only emits once a socket existed (the client is lazy), so a
    // session that never uses realtime never sees the reconnecting pill.
    this.wsSubscription = this.subscriptionClient.connected$.subscribe(connected => {
      this.realtimeDown = !connected;
      if (connected) {
        this.clearRealtimeGrace();
        this.realtimeDegraded.set(false);
      } else if (document.visibilityState === 'visible' && !this.realtimeDegraded()
        && this.subscriptionClient.activeConsumerStreams() !== 0) {
        // A drop while hidden arms nothing — the visibility handler starts the
        // countdown when the user is back to see it. A drop with NO stream wanting
        // realtime arms nothing either: the lazy client closes its idle socket itself
        // (a normal close, moments after the last stream ended — after the count's
        // effect below has already run), and a countdown armed by that close would
        // end in a pill nothing reconnects from and nothing clears.
        this.armRealtimeGrace();
      }
    });

    // A stream that ends TERMINALLY (typed gate rejection, logout) is not coming back.
    // When the LAST one goes, "Reconnecting…" would be a lie — nothing will reconnect
    // until some surface subscribes again — so the pill drops and any pending grace
    // timer dies with it. A later subscribe re-arms via the closed/connected events.
    effect(() => {
      if (this.subscriptionClient.activeConsumerStreams() !== 0) return;
      this.clearRealtimeGrace();
      this.realtimeDegraded.set(false);
    });
  }

  /**
   * Trigger an immediate connectivity check. Called by GlobalErrorHandler
   * when a network error is detected, so the offline / server-unreachable bar
   * appears instantly instead of waiting for the next heartbeat cycle.
   */
  checkNow(): void {
    this.checkConnectivity();
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.backOnlineTimer) clearTimeout(this.backOnlineTimer);
    this.clearRealtimeGrace();
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = null;
  }

  private armRealtimeGrace(): void {
    if (this.realtimeGraceTimer) return;
    this.realtimeGraceTimer = setTimeout(() => {
      this.realtimeGraceTimer = null;
      this.realtimeDegraded.set(true);
    }, REALTIME_GRACE_MS);
  }

  private clearRealtimeGrace(): void {
    if (this.realtimeGraceTimer) {
      clearTimeout(this.realtimeGraceTimer);
      this.realtimeGraceTimer = null;
    }
  }

  private setDisconnected(reason: 'offline' | 'server-unreachable'): void {
    if (this.state() === reason) return; // Already in this disconnected state
    this.state.set(reason);
    this.showBackOnline.set(false);
    if (this.backOnlineTimer) {
      clearTimeout(this.backOnlineTimer);
      this.backOnlineTimer = null;
    }
    this.startHeartbeat(HEARTBEAT_OFFLINE_MS);
  }

  private setOnline(): void {
    const previousState = this.state();
    this.state.set('online');

    if (previousState !== 'online') {
      this.recoveredFrom.set(previousState);
      this.showBackOnline.set(true);
      if (this.backOnlineTimer) clearTimeout(this.backOnlineTimer);
      this.backOnlineTimer = setTimeout(() => {
        this.showBackOnline.set(false);
        this.recoveredFrom.set(null);
      }, BACK_ONLINE_DISPLAY_MS);
    }

    this.startHeartbeat(HEARTBEAT_ONLINE_MS);
  }

  private startHeartbeat(intervalMs: number): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => this.checkConnectivity(), intervalMs);
  }

  private async checkConnectivity(): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), HEARTBEAT_TIMEOUT_MS);
      await fetch(`${environment.apiUrl}/api/health`, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      this.setOnline();
    } catch {
      // Browser says no network → user is offline
      // Browser says online but fetch failed → server is unreachable
      this.setDisconnected(navigator.onLine ? 'server-unreachable' : 'offline');
    }
  }
}
